import { Accordion, AccordionDetails, AccordionSummary, Badge, Box, ListItemButton, ListItemText, Typography } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import FolderOpenIcon from "@mui/icons-material/FolderOpen";
import { CommentableEntity, hasUnreadComments } from "../../utils";
import { SessionState } from "ezStore";

interface CommentsWidgetGroupProps {
	title: string;
	collapsableDisabled?: boolean
	expanded: boolean;
	onToggle: () => void;
	entities: CommentableEntity[];
	selectedEntity?: CommentableEntity;
	selectEntity: (e: CommentableEntity) => void;
	readComments: SessionState["readComments"];
}

const CommentsWidgetGroup = ({
	title,
	collapsableDisabled,
	expanded,
	onToggle,
	entities,
	selectedEntity,
	selectEntity,
	readComments,
}: CommentsWidgetGroupProps) => {

	const hasUnread = hasUnreadComments(entities.flatMap(entity => entity.comments), readComments);

	return (
		<Accordion
			expanded={collapsableDisabled ? true : expanded}
			onChange={onToggle}
			disabled={collapsableDisabled}
			disableGutters
			square
			elevation={0}
			sx={{
				backgroundColor: "transparent",
				borderBottom: "1px solid",
				borderColor: "divider",

				"&:before": {
					display: "none",
				},

				"&.Mui-expanded": {
					margin: 0,
				},

				"&.Mui-disabled": {
					backgroundColor: "transparent",
				},
			}}>
			<AccordionSummary expandIcon={collapsableDisabled ? null : <ExpandMoreIcon sx={{ fontSize: 18, color: "#64748B" }} />}>
				<Badge
					color="error"
					variant="dot"
					invisible={!hasUnread}>
					<FolderOpenIcon sx={{ fontSize: 18 }} />
				</Badge>

				<Typography sx={{ fontSize: 13, fontWeight: 600, ml: 1 }}>
					{title}
				</Typography>
			</AccordionSummary>

			<AccordionDetails sx={{ p: 0, pb: 0 }}>
				{entities.map((entity) => {
					const isSelected = selectedEntity?.id === entity.id;
					const hasUnRead = hasUnreadComments(entity.comments, readComments);

					return (
						<ListItemButton
							key={entity.id}
							onClick={() => selectEntity(entity)}
							selected={isSelected}
							sx={{
								borderLeft: "3px solid",
								borderLeftColor: isSelected ? "primary.main" : "transparent",
								backgroundColor: isSelected ? "action.selected" : "transparent",
								minHeight: 32,
								pl: 3.5,
								pr: 2,
								py: 0.5,
								transition: "all 0.15s ease-in-out",

								"&:hover": {
									backgroundColor: "action.hover",
								},
							}}>
							<Box
								sx={{
									alignItems: "center",
									display: "flex",
									gap: 1.5,
									width: "100%",
								}}>
								<Badge color="error" variant="dot" invisible={!hasUnRead} />

								<ListItemText
									primary={entity.name}
									slotProps={{
										primary: {
											fontSize: 13,
											fontWeight: isSelected ? 600 : 400,
											color: isSelected ? "text.primary" : "text.secondary",
											noWrap: true,
										},
									}} />
							</Box>
						</ListItemButton>
					);
				})}
			</AccordionDetails>
		</Accordion>
	);
};

export default CommentsWidgetGroup;
